import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { format } from 'date-fns';
import {
  ArrowCounterClockwiseIcon,
  CaretDownIcon,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import ConfirmDialog from '@/components/common/ConfirmDialog';

/**
 * One version, reduced to what the list renders. `detail` is the already
 * localized "how it came about · how big it is" line, which is the only part
 * that differs between the things we version.
 */
export interface VersionHistoryEntry {
  id: string;
  versionNumber: number;
  isCurrent: boolean;
  createdByName: string | null;
  createdAt: string | Date;
  detail: string;
  /**
   * When set, this version cannot be restored; the string explains why and
   * replaces the Restore button.
   */
  restoreDisabledReason?: string;
}

/** One saved version, with its restore action. */
function VersionRow({
  entry,
  onRestore,
}: {
  entry: VersionHistoryEntry;
  onRestore: (entry: VersionHistoryEntry) => void;
}) {
  const gt = useGT();

  return (
    <li
      className={
        entry.isCurrent
          ? 'border-l-2 border-l-primary pl-2.5'
          : 'border-l-2 pl-2.5'
      }
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-foreground">
          <T context="version history: version label">
            Version <Var>{entry.versionNumber}</Var>
          </T>
        </p>
        {entry.isCurrent ? (
          <Badge
            variant="secondary"
            className="shrink-0 px-1.5 py-0 text-[10px]"
          >
            <T context="version history: badge on the live version">Current</T>
          </Badge>
        ) : entry.restoreDisabledReason !== undefined ? (
          <span className="shrink-0 text-[10px] text-muted-foreground">
            {entry.restoreDisabledReason}
          </span>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 shrink-0 px-1.5 text-[11px]"
            onClick={() => onRestore(entry)}
          >
            <ArrowCounterClockwiseIcon className="h-3 w-3" />
            <T context="version history: restore this version">Restore</T>
          </Button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">{entry.detail}</p>
      <p className="text-[11px] text-muted-foreground">
        {entry.createdByName ??
          gt('Unknown user', {
            context:
              'version history: fallback author for a deleted/system user',
          })}
        {' · '}
        {format(new Date(entry.createdAt), 'MMM d, yyyy h:mm a')}
      </p>
    </li>
  );
}

/**
 * A sidebar section listing an entity's saved versions, newest first, marking
 * which one is live and offering a confirmed restore on the rest. Collapsed by
 * default so the sidebar's other controls stay in view.
 *
 * Shared by everything with a version history (pricelists, reviews); the
 * caller supplies the entries, the confirmation's wording, and what restoring
 * actually does.
 */
export function VersionHistorySection({
  entries,
  isPending,
  isRestoring,
  confirmMessage,
  onRestore,
}: {
  entries: VersionHistoryEntry[];
  isPending: boolean;
  isRestoring: boolean;
  /** Body of the restore confirmation — what this restore replaces. */
  confirmMessage: string;
  /** Rejecting leaves the confirmation open; the caller reports the failure. */
  onRestore: (entry: VersionHistoryEntry) => Promise<unknown>;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const [pendingEntry, setPendingEntry] = useState<VersionHistoryEntry | null>(
    null,
  );

  async function confirmRestore() {
    if (!pendingEntry) return;
    try {
      await onRestore(pendingEntry);
      setPendingEntry(null);
    } catch {
      // Surfaced by the caller — keep the confirmation up so the user can
      // retry or back out.
    }
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="space-y-2 border-t pt-4"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-1.5 text-left"
        >
          <CaretDownIcon
            className={`h-3 w-3 shrink-0 transition-transform duration-150 ${open ? '' : '-rotate-90'}`}
          />
          <Label className="cursor-pointer text-xs text-muted-foreground">
            <T context="version history section heading">Version History</T>
          </Label>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="space-y-2">
        {isPending ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            <T context="empty state for a version history">No versions yet.</T>
          </p>
        ) : (
          <ul className="space-y-2.5">
            {entries.map((entry) => (
              <VersionRow
                key={entry.id}
                entry={entry}
                onRestore={setPendingEntry}
              />
            ))}
          </ul>
        )}
      </CollapsibleContent>

      <ConfirmDialog
        open={pendingEntry !== null}
        onClose={() => setPendingEntry(null)}
        onConfirm={() => void confirmRestore()}
        closeOnConfirm={false}
        loading={isRestoring}
        title={gt('Restore version {version}?', {
          context: 'confirmation dialog title for restoring a version',
          version: String(pendingEntry?.versionNumber ?? ''),
        })}
        message={confirmMessage}
        confirmText={gt('Restore', {
          context: 'confirm button for restoring a version',
        })}
        cancelText={gt('Cancel', {
          context: 'cancel button for restoring a version',
        })}
      />
    </Collapsible>
  );
}
